import { useEffect, useState } from "react";
import type { DailySummary as DailySummaryType, SalesTrendPoint } from "@ai-pos/shared";
import { dailySummaryApi } from "../api/resources.js";
import { TrendChart } from "../components/TrendChart.js";
import { addDaysToDate, formatShortDate } from "../utils/date.js";

export function DailySummary() {
  const [businessDate, setBusinessDate] = useState("");
  const [summary, setSummary] = useState<DailySummaryType | null>(null);
  const [trendStart, setTrendStart] = useState("");
  const [trendEnd, setTrendEnd] = useState("");
  const [trend, setTrend] = useState<SalesTrendPoint[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printMessage, setPrintMessage] = useState<string | null>(null);

  const loadTrend = (start: string, end: string) => {
    if (!start || !end) return;
    dailySummaryApi
      .getTrend(start, end)
      .then((res) => setTrend(res.points))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "推移の取得に失敗しました"));
  };

  const load = (date?: string) => {
    dailySummaryApi
      .get(date || undefined)
      .then((res) => {
        setSummary(res.summary);
        setBusinessDate(res.summary.businessDate);
        if (!trendStart && !trendEnd) {
          const defaultStart = addDaysToDate(res.summary.businessDate, -6);
          setTrendStart(defaultStart);
          setTrendEnd(res.summary.businessDate);
          loadTrend(defaultStart, res.summary.businessDate);
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), []);

  const handlePrint = async () => {
    setPrinting(true);
    setPrintMessage(null);
    try {
      const res = await dailySummaryApi.print(businessDate || undefined);
      setPrintMessage(res.printResult.message);
    } catch (err) {
      setPrintMessage(err instanceof Error ? err.message : "精算票の印刷に失敗しました");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="stack">
      <h2>日次集計</h2>
      <div className="card row">
        <div className="form-field">
          <label htmlFor="summary-date">営業日</label>
          <input id="summary-date" type="date" value={businessDate} onChange={(e) => setBusinessDate(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={() => load(businessDate)}>
          表示
        </button>
        <button className="btn" onClick={() => void handlePrint()} disabled={printing}>
          {printing ? "印刷中..." : "精算票を印刷"}
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {printMessage && <div className="alert alert-warning">【日次精算】{printMessage}</div>}

      {summary && (
        <>
          <div className="grid-2">
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>総売上</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>¥{summary.totalSales.toLocaleString("ja-JP")}</div>
            </div>
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>会計件数</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.orderCount}件</div>
            </div>
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>客数</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.guestCount}名</div>
            </div>
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>客単価</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>¥{summary.averageSpendPerGuest.toLocaleString("ja-JP")}</div>
            </div>
          </div>

          <div className="stack">
            <div className="card row" style={{ alignItems: "flex-end" }}>
              <h3 style={{ margin: 0, marginRight: "auto" }}>推移グラフ</h3>
              <div className="form-field">
                <label htmlFor="trend-start">開始日</label>
                <input
                  id="trend-start"
                  type="date"
                  value={trendStart}
                  max={trendEnd || undefined}
                  onChange={(e) => setTrendStart(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="trend-end">終了日</label>
                <input
                  id="trend-end"
                  type="date"
                  value={trendEnd}
                  min={trendStart || undefined}
                  onChange={(e) => setTrendEnd(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={() => loadTrend(trendStart, trendEnd)}>
                表示
              </button>
            </div>
            <TrendChart
              title="来客数"
              points={trend.map((p) => ({ label: formatShortDate(p.businessDate), value: p.guestCount }))}
              color="var(--color-primary)"
              formatValue={(v) => `${v}名`}
            />
            <TrendChart
              title="売上"
              points={trend.map((p) => ({ label: formatShortDate(p.businessDate), value: p.totalSales }))}
              color="var(--color-primary-dark)"
              formatValue={(v) => `¥${v.toLocaleString("ja-JP")}`}
            />
            <TrendChart
              title="客単価"
              points={trend.map((p) => ({ label: formatShortDate(p.businessDate), value: p.averageSpendPerGuest }))}
              color="var(--color-warning)"
              formatValue={(v) => `¥${v.toLocaleString("ja-JP")}`}
            />
          </div>

          <div className="grid-2">
            <div className="card">
              <div>値引き額: ¥{summary.discountTotal.toLocaleString("ja-JP")}</div>
              <div>招待・サービス金額: ¥{summary.complimentaryTotal.toLocaleString("ja-JP")}</div>
              <div>取消金額: ¥{summary.voidedTotal.toLocaleString("ja-JP")}</div>
            </div>
            <div className="card">
              <h3>スタッフ別会計数</h3>
              {summary.ordersByStaff.map((s) => (
                <div key={s.staffId} className="cart-summary__row">
                  <span>{s.staffName}</span>
                  <span>{s.orderCount}件</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h3>商品別売上</h3>
            <table>
              <thead>
                <tr>
                  <th>商品</th>
                  <th>数量</th>
                  <th>金額</th>
                </tr>
              </thead>
              <tbody>
                {summary.salesByProduct.map((p) => (
                  <tr key={p.productId}>
                    <td>{p.productName}</td>
                    <td>{p.quantity}</td>
                    <td>¥{p.amount.toLocaleString("ja-JP")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid-2">
            <div className="card">
              <h3>カテゴリー別売上</h3>
              {summary.salesByCategory.map((c) => (
                <div key={c.categoryId} className="cart-summary__row">
                  <span>{c.categoryName}</span>
                  <span>¥{c.amount.toLocaleString("ja-JP")}</span>
                </div>
              ))}
            </div>
            <div className="card">
              <h3>支払方法別売上</h3>
              {summary.salesByPaymentMethod.map((p) => (
                <div key={p.paymentMethodId} className="cart-summary__row">
                  <span>{p.paymentMethodName}</span>
                  <span>¥{p.amount.toLocaleString("ja-JP")}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
